import { store } from '../store';
import { featureFlags } from '../config/featureFlags';
import { adminAuthenticatedFetch } from '../utils/adminAuthenticatedFetch';
import axiosInstance from './axiosConfig';

const API_BASE = featureFlags.general.apiUrl;

function getAdminToken(): string {
  return store.getState().auth.token || '';
}

function withNoCache(url: string): string {
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}_ts=${Date.now()}`;
}

export interface CryptoPortfolioSummary {
  totalOpenValue: number;
  unrealizedPnl: number;
  realizedPnl: number;
  netPnl: number;
  openCount: number;
  closedCount: number;
}

export interface CryptoPortfolioAssetShare {
  symbol: string;
  sharePct: number;
}

export interface CryptoPortfolioAllocation {
  botManagedPct: number;
  directPct: number;
  topAssets: CryptoPortfolioAssetShare[];
}

export interface CryptoPortfolioPositionFill {
  openedAt?: string | null;
  amountUsd?: number | null;
  entryPrice?: number | null;
}

export interface CryptoPortfolioPositionRow {
  hedgePositionId?: number | null;
  strategyId?: number | null;
  strategyName?: string | null;
  source: 'BOT' | 'DIRECT' | string;
  symbol: string;
  side: string;
  status: string;
  openedAt?: string | null;
  closedAt?: string | null;
  entryPrice?: number | null;
  currentPrice?: number | null;
  exitPrice?: number | null;
  sizeUsd?: number | null;
  leverage?: number | null;
  unrealizedPnl?: number | null;
  realizedPnl?: number | null;
  kucoinAccountName?: string | null;
  canClose?: boolean;
  canViewStrategy?: boolean;
  targetPct?: number | null;
  pnlOnSizePct?: number | null;
  targetProgressPct?: number | null;
  targetAchieved?: boolean;
  autoCloseOnTarget?: boolean;
  canToggleAutoClose?: boolean;
  stopLossPct?: number | null;
  trailingStopEnabled?: boolean;
  trailingStopActivationPct?: number | null;
  trailingStopDistancePct?: number | null;
  trailingStopActive?: boolean;
  breakEntryExitEnabled?: boolean;
  canConfigureExitRules?: boolean;
  peakPriceSinceOpen?: number | null;
  troughPriceSinceOpen?: number | null;
  activityBotId?: number | null;
  activityBotLabel?: string | null;
  fills?: CryptoPortfolioPositionFill[];
}

export interface SetPositionExitRulesRequest {
  stopLossPct?: number | null;
  trailingStopEnabled?: boolean;
  trailingStopActivationPct?: number;
  trailingStopDistancePct?: number;
  breakEntryExitEnabled?: boolean;
}

export interface CryptoPortfolioProfitTarget {
  configured: boolean;
  targetPct?: number | null;
  baseOpenValue?: number | null;
  targetAmountUsd?: number | null;
  currentNetPnl?: number | null;
  progressPct?: number | null;
  remainingUsd?: number | null;
  achieved?: boolean;
  showAlert?: boolean;
  achievedAt?: string | null;
}

export interface CryptoPortfolioPayload {
  summary: CryptoPortfolioSummary;
  openPositions: CryptoPortfolioPositionRow[];
  closedPositions: CryptoPortfolioPositionRow[];
  allocation: CryptoPortfolioAllocation;
  profitTarget?: CryptoPortfolioProfitTarget | null;
  updatedAt: number;
}

export interface ActivityBotSectorOption {
  id: string;
  label: string;
}

export interface ActivityBotKucoinAccount {
  id: number;
  name: string;
  shared?: boolean;
  selectable?: boolean;
}

export interface ActivityBotPreviewLeg {
  symbol: string;
  side: string;
  hot?: boolean;
  change24h?: number;
  filterOverall?: 'PASS' | 'WAIT' | 'REJECT' | 'BLOCK_ALL' | string;
  filterMessage?: string | null;
}

export interface FilterProfileConfig {
  schemaVersion?: number;
  entry?: {
    enabled?: boolean;
    fundingRateEnabled?: boolean;
    maxFundingRate?: number;
    rsiEnabled?: boolean;
    rsiLongMin?: number;
    rsiShortMax?: number;
    emaEnabled?: boolean;
    emaPeriod?: number;
    peakTroughTimingEnabled?: boolean;
  };
  marketGates?: {
    participationEnabled?: boolean;
    minParticipationRate?: number;
    majorCoinParticipationEnabled?: boolean;
    minMajorCoinParticipationRate?: number;
  };
  regime?: {
    blockOpensInLowParticipation?: boolean;
    blockOpensInBearishBtc?: boolean;
    blockLongInBearishBtc?: boolean;
    /** Block SHORT entries unless BTC regime is BEARISH. */
    blockShortUnlessBearishBtc?: boolean;
  };
  microReversal?: {
    enabled?: boolean;
    minDropPercent?: number;
    maxDropPercent?: number;
    lookbackCandles?: number;
  };
  volatility?: {
    enabled?: boolean;
    minMarketVolatility?: number;
    maxMarketVolatility?: number;
  };
  btcDump?: {
    enabled?: boolean;
    dumpThreshold?: number;
    cooldownMinutes?: number;
  };
  /** Reactive risk gate — pause opens after consecutive losing closes. Default off. */
  lossCooldown?: {
    enabled?: boolean;
    consecutiveLosses?: number;
    lossWindowHours?: number;
    cooldownHours?: number;
  };
  oracle?: {
    enabled?: boolean;
    confidenceThreshold?: number;
    weightMomentum?: number | null;
    weightRsi?: number | null;
    weightVolume?: number | null;
    weightFunding?: number | null;
    weightEma?: number | null;
    shortlistSize?: number;
  };
}

export interface TradingFilterCatalogSection {
  id: string;
  label: string;
  description?: string;
}

export interface TradingFilterCatalogResponse {
  enabled?: boolean;
  sections?: TradingFilterCatalogSection[];
  profiles?: TradingFilterProfile[];
}

export interface TradingFilterProfile {
  id: number;
  name: string;
  description?: string | null;
  systemTemplate?: boolean;
  adminUserId?: number | null;
  schemaVersion?: number;
  config?: FilterProfileConfig;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface ActivityBotPreviewResponse {
  marketRegime?: string | null;
  filterProfileName?: string | null;
  legs: ActivityBotPreviewLeg[];
}

export interface ActivityBotPositionHistoryRow {
  hedgePositionId?: number | null;
  symbol: string;
  side: string;
  status: string;
  openedAt?: string | null;
  closedAt?: string | null;
  sizeUsd?: number | null;
  leverage?: number | null;
  entryPrice?: number | null;
  exitPrice?: number | null;
  currentPrice?: number | null;
  realizedPnl?: number | null;
  unrealizedPnl?: number | null;
  pnlOnSizePct?: number | null;
}

export interface ActivityBotLogsPayload {
  botId: number;
  botLabel?: string | null;
  requestedLines?: number;
  totalLines?: number;
  logs: string[];
}

export interface CryptoPortfolioActivityBot {
  id: number;
  referenceName?: string | null;
  displayLabel?: string | null;
  kucoinConfigId: number;
  kucoinAccountName?: string;
  amountUsd: number;
  leverage?: number;
  sectorId: string;
  sectorLabel?: string;
  totalPositions: number;
  longCount: number;
  shortCount: number;
  targetProfitPct: number;
  stopLossPct?: number | null;
  trailingStopEnabled?: boolean;
  trailingStopActivationPct?: number | null;
  trailingStopDistancePct?: number | null;
  maxBotLossUsd?: number | null;
  maxBotLossPct?: number | null;
  cycleNetPnl?: number | null;
  cycleDeployedUsd?: number | null;
  continuousRun?: boolean;
  entryMode?: 'ACTIVITY_ONLY' | 'PEAK_TROUGH_TIMING' | string;
  filterProfileId?: number | null;
  filterProfileName?: string | null;
  macroBtcBridgeEnabled?: boolean;
  macroCommodityBridgeEnabled?: boolean;
  status: 'DRAFT' | 'RUNNING' | 'PAUSED' | 'COMPLETED' | string;
  positionsOpened: number;
  longOpened?: number;
  shortOpened?: number;
  lastRunAt?: string | null;
  lastError?: string | null;
  completedAt?: string | null;
  createdAt?: string | null;
  previewLegs?: ActivityBotPreviewLeg[];
  openLegs?: string[];
  aiAutoTuneEnabled?: boolean;
  aiAutoTuneRequireApproval?: boolean;
  aiAutoTuneLastRegime?: string | null;
  aiAutoTuneLastAppliedAt?: number | null;
  aiAutoTunePending?: boolean;
  lowParticipationMode?: string | null;
  lowParticipationAutoBlockDays?: number | null;
  aiRegimeLongBlockEnabled?: boolean;
  aiRegimeLongBlockList?: string | null;
  scalperModeEnabled?: boolean;
  aiPerformanceTuneEnabled?: boolean;
  aiPerformanceTuneWindow?: number;
  aiPerformanceTuneMinTrades?: number;
  aiPerformanceSideBlockEnabled?: boolean;
  aiPerformanceTuneLlmEnabled?: boolean;
  aiPerformanceTuneHistoryLearningEnabled?: boolean;
}

export interface ActivityBotAiTuneHistoryEntry {
  id: number;
  appliedAt?: string;
  marketRegime?: string;
  trigger?: string;
  outcome?: string;
  runTriggerReason?: string;
  actor?: string;
  changes?: Array<{ field: string; label: string; before: string; after: string }>;
  performanceMeta?: Record<string, unknown>;
  performanceAtApply?: Record<string, unknown>;
  performanceAfter?: Record<string, unknown>;
}

export interface UpdateActivityBotAiAutoTuneRequest {
  aiAutoTuneEnabled?: boolean;
  aiAutoTuneRequireApproval?: boolean;
  aiPerformanceTuneEnabled?: boolean;
  aiPerformanceTuneWindow?: number;
  aiPerformanceTuneMinTrades?: number;
  aiPerformanceSideBlockEnabled?: boolean;
  aiPerformanceTuneLlmEnabled?: boolean;
  aiPerformanceTuneHistoryLearningEnabled?: boolean;
  lowParticipationMode?: string;
  lowParticipationAutoBlockDays?: number;
  aiRegimeLongBlockEnabled?: boolean;
  aiRegimeLongBlockList?: string;
  scalperModeEnabled?: boolean;
  scalperTakeProfit?: number;
  scalperTrailingStopLoss?: number;
  scalperMaxOpenPositions?: number;
  scalperRegimeList?: string;
}

export interface SaveActivityBotRequest {
  referenceName?: string | null;
  kucoinConfigId: number;
  amountUsd: number;
  leverage?: number;
  sectorId: string;
  totalPositions: number;
  longCount: number;
  shortCount: number;
  targetProfitPct: number;
  stopLossPct?: number | null;
  trailingStopEnabled?: boolean;
  trailingStopActivationPct?: number | null;
  trailingStopDistancePct?: number | null;
  maxBotLossUsd?: number | null;
  maxBotLossPct?: number | null;
  continuousRun?: boolean;
  entryMode?: 'ACTIVITY_ONLY' | 'PEAK_TROUGH_TIMING' | string;
  filterProfileId?: number | null;
  macroBtcBridgeEnabled?: boolean;
  macroCommodityBridgeEnabled?: boolean;
}

export interface ActivityBotPreviewRequest {
  sectorId: string;
  totalPositions: number;
  longCount: number;
  shortCount: number;
  filterProfileId?: number | null;
  entryMode?: 'ACTIVITY_ONLY' | 'PEAK_TROUGH_TIMING' | string;
}

export interface GrowthChallengeLadderStep {
  atUsd: number;
  lockUsd: number;
  reached?: boolean | null;
}

export interface GrowthChallengeBotSummary {
  botId: number;
  label?: string | null;
  sectorId?: string | null;
  sectorLabel?: string | null;
  status?: string | null;
  amountUsd?: number | null;
  netPnlUsd?: number | null;
  openLegs?: number;
  lastError?: string | null;
}

export interface GrowthChallenge {
  id: number;
  name?: string | null;
  kucoinConfigId: number;
  kucoinAccountName?: string | null;
  startBalanceUsd: number;
  targetBalanceUsd: number;
  floorBalanceUsd: number;
  lockedProfitUsd?: number | null;
  equityUsd?: number | null;
  riskEquityUsd?: number | null;
  progressPct?: number | null;
  /** Live KuCoin account equity (fee-inclusive), frozen at end for finished challenges. */
  accountEquityUsd?: number | null;
  /** Real profit after fees/funding: account equity delta since challenge start. */
  realPnlUsd?: number | null;
  status: 'ACTIVE' | 'PAUSED' | 'TARGET_REACHED' | 'STOPPED_FLOOR' | 'STOPPED_MANUAL' | string;
  statusNote?: string | null;
  ladder?: GrowthChallengeLadderStep[];
  dailyLossLimitPct?: number | null;
  daysElapsed?: number | null;
  startedAt?: string | null;
  endedAt?: string | null;
  lastEvaluatedAt?: string | null;
  createdAt?: string | null;
  bots?: GrowthChallengeBotSummary[];
}

export interface CreateGrowthChallengeRequest {
  name?: string | null;
  kucoinConfigId: number;
  startBalanceUsd: number;
  targetBalanceUsd: number;
  floorBalanceUsd: number;
  sectorIds: string[];
  ladder?: GrowthChallengeLadderStep[];
  confirmationText: string;
}

async function adminFetch(url: string, init: RequestInit = {}, timeoutMs = 20000): Promise<Response> {
  return adminAuthenticatedFetch(url, init, timeoutMs);
}

function extractApiErrorMessage(err: any, fallback: string): string {
  // Zogzila API error responses are shaped like:
  // { code, message, status, data: null, errors: ["Detailed error"] }
  const errors = err?.errors;
  if (Array.isArray(errors) && errors.length > 0) {
    const first = errors.find(e => typeof e === 'string' && e.trim().length > 0) ?? errors[0];
    if (typeof first === 'string') return first;
    return String(first);
  }
  if (typeof err?.message === 'string' && err.message.trim().length > 0) return err.message;
  return fallback;
}

export interface PositionPricePoint {
  time: number;
  price: number;
  label: string;
}

export async function fetchPositionPriceHistory(
  symbol: string,
  openedAt: string | null | undefined,
  closedAt: string | null | undefined,
  isOpen: boolean,
  isAdminContext = false,
): Promise<PositionPricePoint[]> {
  const params = new URLSearchParams({
    symbol,
    open: String(isOpen),
  });
  if (openedAt) params.set('openedAt', openedAt);
  if (closedAt) params.set('closedAt', closedAt);

  const path = isAdminContext
    ? `${API_BASE}/api/admin/crypto-portfolio/price-history`
    : `${API_BASE}/api/retail/crypto-portfolio/price-history`;

  if (isAdminContext) {
    const res = await adminFetch(withNoCache(`${path}?${params.toString()}`), {
      headers: { Authorization: `Bearer ${getAdminToken()}` },
    });
    if (!res.ok) throw new Error(`${res.status}`);
    const data = await res.json();
    return Array.isArray(data) ? data as PositionPricePoint[] : [];
  }

  const res = await axiosInstance.get(withNoCache(`${path}?${params.toString()}`));
  const data = res.data;
  return Array.isArray(data) ? data as PositionPricePoint[] : [];
}

export const cryptoPortfolioService = {
  getRetailPortfolio: async (): Promise<CryptoPortfolioPayload> => {
    const res = await axiosInstance.get(withNoCache(`${API_BASE}/api/retail/crypto-portfolio`));
    return res.data as CryptoPortfolioPayload;
  },

  getAdminPortfolio: async (): Promise<CryptoPortfolioPayload> => {
    const res = await adminFetch(
      withNoCache(`${API_BASE}/api/admin/crypto-portfolio`),
      { headers: { Authorization: `Bearer ${getAdminToken()}` } },
      20000
    );
    if (!res.ok) throw new Error(`${res.status}`);
    return res.json() as Promise<CryptoPortfolioPayload>;
  },

  setAdminProfitTarget: async (targetPct: number): Promise<CryptoPortfolioProfitTarget> => {
    const res = await adminFetch(`${API_BASE}/api/admin/crypto-portfolio/profit-target`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${getAdminToken()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ targetPct }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || `${res.status}`);
    }
    return res.json() as Promise<CryptoPortfolioProfitTarget>;
  },

  clearAdminProfitTarget: async (): Promise<CryptoPortfolioProfitTarget> => {
    const res = await adminFetch(`${API_BASE}/api/admin/crypto-portfolio/profit-target`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${getAdminToken()}` },
    });
    if (!res.ok) throw new Error(`${res.status}`);
    return res.json() as Promise<CryptoPortfolioProfitTarget>;
  },

  dismissAdminProfitTargetAlert: async (): Promise<CryptoPortfolioProfitTarget> => {
    const res = await adminFetch(`${API_BASE}/api/admin/crypto-portfolio/profit-target/dismiss-alert`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${getAdminToken()}` },
    });
    if (!res.ok) throw new Error(`${res.status}`);
    return res.json() as Promise<CryptoPortfolioProfitTarget>;
  },

  setPositionAutoClose: async (
    hedgePositionId: number,
    enabled: boolean,
  ): Promise<CryptoPortfolioPositionRow> => {
    const res = await adminFetch(`${API_BASE}/api/admin/crypto-portfolio/positions/${hedgePositionId}/auto-close`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${getAdminToken()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ enabled }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || `${res.status}`);
    }
    return res.json() as Promise<CryptoPortfolioPositionRow>;
  },

  setPositionExitRules: async (
    hedgePositionId: number,
    payload: SetPositionExitRulesRequest,
  ): Promise<CryptoPortfolioPositionRow> => {
    const res = await adminFetch(`${API_BASE}/api/admin/crypto-portfolio/positions/${hedgePositionId}/exit-rules`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${getAdminToken()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || `${res.status}`);
    }
    return res.json() as Promise<CryptoPortfolioPositionRow>;
  },

  listActivityBotSectors: async (): Promise<ActivityBotSectorOption[]> => {
    const res = await adminFetch(`${API_BASE}/api/admin/crypto-portfolio/activity-bots/sectors`, {
      headers: { Authorization: `Bearer ${getAdminToken()}` },
    });
    if (!res.ok) throw new Error(`${res.status}`);
    const data = await res.json();
    return Array.isArray(data) ? data as ActivityBotSectorOption[] : [];
  },

  listActivityBotKucoinAccounts: async (): Promise<ActivityBotKucoinAccount[]> => {
    const res = await adminFetch(`${API_BASE}/api/admin/crypto-portfolio/activity-bots/kucoin-accounts`, {
      headers: { Authorization: `Bearer ${getAdminToken()}` },
    });
    if (!res.ok) throw new Error(`${res.status}`);
    const data = await res.json();
    return Array.isArray(data) ? data as ActivityBotKucoinAccount[] : [];
  },

  listActivityBots: async (): Promise<CryptoPortfolioActivityBot[]> => {
    const res = await adminFetch(withNoCache(`${API_BASE}/api/admin/crypto-portfolio/activity-bots`), {
      headers: { Authorization: `Bearer ${getAdminToken()}` },
    });
    if (!res.ok) throw new Error(`${res.status}`);
    const data = await res.json();
    return Array.isArray(data) ? data as CryptoPortfolioActivityBot[] : [];
  },

  getActivityBotHistory: async (botId: number): Promise<ActivityBotPositionHistoryRow[]> => {
    const res = await adminFetch(withNoCache(`${API_BASE}/api/admin/crypto-portfolio/activity-bots/${botId}/history`), {
      headers: { Authorization: `Bearer ${getAdminToken()}` },
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || `${res.status}`);
    }
    const data = await res.json();
    return Array.isArray(data) ? data as ActivityBotPositionHistoryRow[] : [];
  },

  getActivityBotLogs: async (botId: number, lines = 500): Promise<ActivityBotLogsPayload> => {
    const safeLines = Math.max(50, Math.min(lines, 2000));
    const res = await adminFetch(
      withNoCache(`${API_BASE}/api/admin/crypto-portfolio/activity-bots/${botId}/logs?lines=${safeLines}`),
      { headers: { Authorization: `Bearer ${getAdminToken()}` } },
    );
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(extractApiErrorMessage(err, `${res.status}`));
    }
    const data = await res.json();
    return {
      botId: Number(data?.botId ?? botId),
      botLabel: data?.botLabel ?? null,
      requestedLines: Number(data?.requestedLines ?? safeLines),
      totalLines: Number(data?.totalLines ?? 0),
      logs: Array.isArray(data?.logs) ? data.logs as string[] : [],
    };
  },

  previewActivityBot: async (payload: ActivityBotPreviewRequest): Promise<ActivityBotPreviewResponse> => {
    const res = await adminFetch(`${API_BASE}/api/admin/crypto-portfolio/activity-bots/preview`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${getAdminToken()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || `${res.status}`);
    }
    const data = await res.json();
    if (data && Array.isArray(data.legs)) {
      return data as ActivityBotPreviewResponse;
    }
    if (Array.isArray(data)) {
      return { legs: data as ActivityBotPreviewLeg[] };
    }
    return { legs: [] };
  },

  listFilterProfiles: async (): Promise<TradingFilterProfile[]> => {
    const res = await adminFetch(`${API_BASE}/api/admin/crypto-portfolio/filter-profiles`, {
      headers: { Authorization: `Bearer ${getAdminToken()}` },
    });
    if (!res.ok) throw new Error(`${res.status}`);
    const data = await res.json();
    return Array.isArray(data) ? data as TradingFilterProfile[] : [];
  },

  getFilterProfileCatalog: async (): Promise<TradingFilterCatalogResponse> => {
    const res = await adminFetch(`${API_BASE}/api/admin/crypto-portfolio/filter-profiles/catalog`, {
      headers: { Authorization: `Bearer ${getAdminToken()}` },
    });
    if (!res.ok) throw new Error(`${res.status}`);
    return res.json() as Promise<TradingFilterCatalogResponse>;
  },

  cloneFilterProfile: async (profileId: number): Promise<TradingFilterProfile> => {
    const res = await adminFetch(`${API_BASE}/api/admin/crypto-portfolio/filter-profiles/${profileId}/clone`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${getAdminToken()}` },
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(extractApiErrorMessage(err, `${res.status}`));
    }
    return res.json() as Promise<TradingFilterProfile>;
  },

  createFilterProfile: async (payload: {
    name: string;
    description?: string | null;
    config: FilterProfileConfig;
  }): Promise<TradingFilterProfile> => {
    const res = await adminFetch(`${API_BASE}/api/admin/crypto-portfolio/filter-profiles`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${getAdminToken()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(extractApiErrorMessage(err, `${res.status}`));
    }
    return res.json() as Promise<TradingFilterProfile>;
  },

  updateFilterProfile: async (
    profileId: number,
    payload: { name: string; description?: string | null; config: FilterProfileConfig },
  ): Promise<TradingFilterProfile> => {
    const res = await adminFetch(`${API_BASE}/api/admin/crypto-portfolio/filter-profiles/${profileId}`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${getAdminToken()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(extractApiErrorMessage(err, `${res.status}`));
    }
    return res.json() as Promise<TradingFilterProfile>;
  },

  deleteFilterProfile: async (profileId: number): Promise<void> => {
    const res = await adminFetch(`${API_BASE}/api/admin/crypto-portfolio/filter-profiles/${profileId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${getAdminToken()}` },
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(extractApiErrorMessage(err, `${res.status}`));
    }
  },

  createActivityBot: async (payload: SaveActivityBotRequest): Promise<CryptoPortfolioActivityBot> => {
    const res = await adminFetch(`${API_BASE}/api/admin/crypto-portfolio/activity-bots`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${getAdminToken()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(extractApiErrorMessage(err, `${res.status}`));
    }
    return res.json() as Promise<CryptoPortfolioActivityBot>;
  },

  updateActivityBot: async (botId: number, payload: SaveActivityBotRequest): Promise<CryptoPortfolioActivityBot> => {
    const res = await adminFetch(`${API_BASE}/api/admin/crypto-portfolio/activity-bots/${botId}`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${getAdminToken()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(extractApiErrorMessage(err, `${res.status}`));
    }
    return res.json() as Promise<CryptoPortfolioActivityBot>;
  },

  cloneActivityBot: async (botId: number): Promise<CryptoPortfolioActivityBot> => {
    const res = await adminFetch(`${API_BASE}/api/admin/crypto-portfolio/activity-bots/${botId}/clone`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${getAdminToken()}` },
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(extractApiErrorMessage(err, `${res.status}`));
    }
    return res.json() as Promise<CryptoPortfolioActivityBot>;
  },

  startActivityBot: async (botId: number): Promise<CryptoPortfolioActivityBot> => {
    const res = await adminFetch(`${API_BASE}/api/admin/crypto-portfolio/activity-bots/${botId}/start`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${getAdminToken()}` },
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(extractApiErrorMessage(err, `${res.status}`));
    }
    return res.json() as Promise<CryptoPortfolioActivityBot>;
  },

  pauseActivityBot: async (botId: number): Promise<CryptoPortfolioActivityBot> => {
    const res = await adminFetch(`${API_BASE}/api/admin/crypto-portfolio/activity-bots/${botId}/pause`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${getAdminToken()}` },
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(extractApiErrorMessage(err, `${res.status}`));
    }
    return res.json() as Promise<CryptoPortfolioActivityBot>;
  },

  setActivityBotContinuousRun: async (botId: number, continuousRun: boolean): Promise<CryptoPortfolioActivityBot> => {
    const res = await adminFetch(`${API_BASE}/api/admin/crypto-portfolio/activity-bots/${botId}/continuous`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${getAdminToken()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ continuousRun }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(extractApiErrorMessage(err, `${res.status}`));
    }
    return res.json() as Promise<CryptoPortfolioActivityBot>;
  },

  deleteActivityBot: async (botId: number): Promise<void> => {
    const res = await adminFetch(`${API_BASE}/api/admin/crypto-portfolio/activity-bots/${botId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${getAdminToken()}` },
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(extractApiErrorMessage(err, `${res.status}`));
    }
  },

  getActivityBotAiAutoTuneGlobalSettings: async (): Promise<Record<string, unknown>> => {
    const res = await adminFetch(`${API_BASE}/api/admin/crypto-portfolio/activity-bots/ai-auto-tune/global-settings`);
    if (!res.ok) throw new Error(`${res.status}`);
    return res.json() as Promise<Record<string, unknown>>;
  },

  updateActivityBotAiAutoTuneSettings: async (
    botId: number,
    settings: UpdateActivityBotAiAutoTuneRequest,
  ): Promise<CryptoPortfolioActivityBot> => {
    const res = await adminFetch(`${API_BASE}/api/admin/crypto-portfolio/activity-bots/${botId}/config/ai-auto-tune`, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${getAdminToken()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(settings),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(extractApiErrorMessage(err, `${res.status}`));
    }
    return res.json() as Promise<CryptoPortfolioActivityBot>;
  },

  getActivityBotAiAutoTunePending: async (botId: number): Promise<{ pending: Record<string, unknown> | null }> => {
    const res = await adminFetch(`${API_BASE}/api/admin/crypto-portfolio/activity-bots/${botId}/ai-auto-tune/pending`);
    if (!res.ok) throw new Error(`${res.status}`);
    return res.json() as Promise<{ pending: Record<string, unknown> | null }>;
  },

  applyActivityBotAiAutoTunePending: async (botId: number): Promise<CryptoPortfolioActivityBot> => {
    const res = await adminFetch(`${API_BASE}/api/admin/crypto-portfolio/activity-bots/${botId}/ai-auto-tune/apply-pending`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${getAdminToken()}` },
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(extractApiErrorMessage(err, `${res.status}`));
    }
    return res.json() as Promise<CryptoPortfolioActivityBot>;
  },

  dismissActivityBotAiAutoTunePending: async (botId: number): Promise<CryptoPortfolioActivityBot> => {
    const res = await adminFetch(`${API_BASE}/api/admin/crypto-portfolio/activity-bots/${botId}/ai-auto-tune/dismiss-pending`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${getAdminToken()}` },
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(extractApiErrorMessage(err, `${res.status}`));
    }
    return res.json() as Promise<CryptoPortfolioActivityBot>;
  },

  runActivityBotAiAutoTuneNow: async (botId: number): Promise<{ outcome?: string; message?: string; bot?: CryptoPortfolioActivityBot; pending?: Record<string, unknown> | null }> => {
    const res = await adminFetch(`${API_BASE}/api/admin/crypto-portfolio/activity-bots/${botId}/ai-auto-tune/run-now`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${getAdminToken()}` },
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(extractApiErrorMessage(err, `${res.status}`));
    }
    return res.json() as Promise<{ outcome?: string; message?: string; bot?: CryptoPortfolioActivityBot; pending?: Record<string, unknown> | null }>;
  },

  getActivityBot: async (botId: number): Promise<CryptoPortfolioActivityBot> => {
    const res = await adminFetch(withNoCache(`${API_BASE}/api/admin/crypto-portfolio/activity-bots/${botId}`), {
      headers: { Authorization: `Bearer ${getAdminToken()}` },
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(extractApiErrorMessage(err, `${res.status}`));
    }
    return res.json() as Promise<CryptoPortfolioActivityBot>;
  },

  getActivityBotAiPerformanceSnapshot: async (
    botId: number,
    window = '12',
  ): Promise<Record<string, unknown>> => {
    const res = await adminFetch(
      `${API_BASE}/api/admin/crypto-portfolio/activity-bots/${botId}/ai-auto-tune/performance-snapshot?window=${encodeURIComponent(window)}`,
    );
    if (!res.ok) throw new Error(`${res.status}`);
    return res.json() as Promise<Record<string, unknown>>;
  },

  getActivityBotAiAutoTuneHistory: async (
    botId: number,
    page = 0,
    size = 10,
  ): Promise<{ items: ActivityBotAiTuneHistoryEntry[]; totalPages: number }> => {
    const res = await adminFetch(
      `${API_BASE}/api/admin/crypto-portfolio/activity-bots/${botId}/ai-auto-tune/history?page=${page}&size=${size}`,
    );
    if (!res.ok) throw new Error(`${res.status}`);
    return res.json() as Promise<{ items: ActivityBotAiTuneHistoryEntry[]; totalPages: number }>;
  },

  getActivityBotAiRegimeReview: async (botId: number): Promise<Record<string, unknown>> => {
    const res = await adminFetch(`${API_BASE}/api/admin/crypto-portfolio/activity-bots/${botId}/ai-auto-tune/regime-review`);
    if (!res.ok) throw new Error(`${res.status}`);
    return res.json() as Promise<Record<string, unknown>>;
  },

  listGrowthChallenges: async (): Promise<GrowthChallenge[]> => {
    const res = await adminFetch(withNoCache(`${API_BASE}/api/admin/crypto-challenge`), {
      headers: { Authorization: `Bearer ${getAdminToken()}` },
    });
    if (!res.ok) throw new Error(`${res.status}`);
    const data = await res.json();
    return Array.isArray(data) ? data as GrowthChallenge[] : [];
  },

  getGrowthChallenge: async (challengeId: number): Promise<GrowthChallenge> => {
    const res = await adminFetch(withNoCache(`${API_BASE}/api/admin/crypto-challenge/${challengeId}`), {
      headers: { Authorization: `Bearer ${getAdminToken()}` },
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(extractApiErrorMessage(err, `${res.status}`));
    }
    return res.json() as Promise<GrowthChallenge>;
  },

  createGrowthChallenge: async (payload: CreateGrowthChallengeRequest): Promise<GrowthChallenge> => {
    const res = await adminFetch(`${API_BASE}/api/admin/crypto-challenge`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${getAdminToken()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    }, 60000);
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(extractApiErrorMessage(err, `${res.status}`));
    }
    return res.json() as Promise<GrowthChallenge>;
  },

  pauseGrowthChallenge: async (challengeId: number): Promise<GrowthChallenge> => {
    const res = await adminFetch(`${API_BASE}/api/admin/crypto-challenge/${challengeId}/pause`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${getAdminToken()}` },
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(extractApiErrorMessage(err, `${res.status}`));
    }
    return res.json() as Promise<GrowthChallenge>;
  },

  resumeGrowthChallenge: async (challengeId: number): Promise<GrowthChallenge> => {
    const res = await adminFetch(`${API_BASE}/api/admin/crypto-challenge/${challengeId}/resume`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${getAdminToken()}` },
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(extractApiErrorMessage(err, `${res.status}`));
    }
    return res.json() as Promise<GrowthChallenge>;
  },

  stopGrowthChallenge: async (challengeId: number): Promise<GrowthChallenge> => {
    const res = await adminFetch(`${API_BASE}/api/admin/crypto-challenge/${challengeId}/stop`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${getAdminToken()}` },
    }, 60000);
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(extractApiErrorMessage(err, `${res.status}`));
    }
    return res.json() as Promise<GrowthChallenge>;
  },

  evaluateGrowthChallenge: async (challengeId: number): Promise<GrowthChallenge> => {
    const res = await adminFetch(`${API_BASE}/api/admin/crypto-challenge/${challengeId}/evaluate`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${getAdminToken()}` },
    }, 60000);
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(extractApiErrorMessage(err, `${res.status}`));
    }
    return res.json() as Promise<GrowthChallenge>;
  },

  fetchPositionPriceHistory,
};
